'use client';

import { Group, UnstyledButton } from '@mantine/core';
import { Menu } from 'lucide-react';
import NotificationBell from '@components/notifications/NotificationBell';
import TrialBadge from '@components/subscription/TrialBadge';
import { PatchLogHistoryButton } from '@/features/patch-logs';
import { useTheme } from '@contexts/ThemeContext';

type DashboardHeaderProps = {
  /** Show the drawer toggle — mobile/tablet only, and only while it is closed. */
  showMenuButton?: boolean;
  onMenuClick?: () => void;
};

/**
 * The dashboard's main header strip.
 *
 * It sits above every dashboard page (mounted once in AppMain) and holds the
 * controls that belong to the shell rather than to a page — the mobile
 * navigation toggle on the left, the changelog history and the
 * incoming-response notification bell on the right. It is deliberately slim and mostly transparent so it reads as part
 * of the page background rather than a second chrome bar competing with the
 * sidebar.
 */
export default function DashboardHeader({ showMenuButton = false, onMenuClick }: DashboardHeaderProps) {
  const { colors: themeColors } = useTheme();

  return (
    <Group
      component="header"
      justify="flex-end"
      align="center"
      gap="xs"
      h={48}
      px="md"
      wrap="nowrap"
      style={{
        position: 'sticky',
        top: 0,
        // Above page content, but below the AppShell navigation drawer
        // (z-100), which must cover this strip once it slides open.
        zIndex: 60,
        backgroundColor: `${themeColors.background}cc`,
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
        borderBottom: `1px solid ${themeColors.border}`,
      }}
    >
      {showMenuButton && (
        <UnstyledButton
          type="button"
          onClick={onMenuClick}
          aria-label="Open navigation menu"
          className="flex items-center justify-center rounded-lg transition-transform active:scale-95"
          // Pushes the bell to the far edge while this sits flush left.
          mr="auto"
          style={{
            width: 36,
            height: 36,
            flexShrink: 0,
            backgroundColor: themeColors.card,
            color: themeColors.text,
            border: `1px solid ${themeColors.border}`,
          }}
        >
          <Menu size={18} strokeWidth={1.5} />
        </UnstyledButton>
      )}
      <TrialBadge />
      <PatchLogHistoryButton />
      <NotificationBell />
    </Group>
  );
}
